import { NotificationType, type types } from "@fraction/shared";

import { claimBrowserNotification } from "./notificationChannel";

/**
 * Request browser notification permission if not yet decided.
 * Returns true if permission is granted.
 */
export function requestNotificationPermission(): Promise<boolean> {
  if (!("Notification" in window)) return Promise.resolve(false);
  if (Notification.permission === "granted") return Promise.resolve(true);
  if (Notification.permission === "denied") return Promise.resolve(false);
  return Notification.requestPermission().then((p) => p === "granted");
}

function getLabel(notification: types.WsNotification): string {
  const name = notification.applicantNames;
  if (name) return ` - ${name}`;
  if (notification.applicationEid) return ` (${notification.applicationEid})`;
  return "";
}

function getTitleAndBody(notification: types.WsNotification): { title: string; body: string } | null {
  const label = getLabel(notification);

  switch (notification.type) {
    case NotificationType.EMAIL_RECEIVED:
      return {
        title: `New email${label}`,
        body: `${notification.senderEmail}: ${notification.subject}`,
      };
    case NotificationType.CHAT_MESSAGE_RECEIVED:
      return {
        title: `Chat message${label}`,
        body: `${notification.senderName}: ${notification.messagePreview}`,
      };
    case NotificationType.APPLICATION_STATUS_CHANGE:
      return {
        title: `Status changed${label}`,
        body: `${notification.fromStatus} → ${notification.toStatus}`,
      };
    case NotificationType.COMMITMENT_LETTER_RATE_MISMATCH:
      return {
        title: `Commitment rate mismatch${label}`,
        body: `${notification.filename}: ${notification.documentRate} vs ${notification.applicationRate}`,
      };
    default:
      return null;
  }
}

/**
 * Show a browser notification for a WS notification.
 * Only fires when the tab is not focused and permission is granted.
 */
export function showBrowserNotification(notification: types.WsNotification): void {
  if (!("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  if (document.hasFocus()) return;
  if (!claimBrowserNotification(notification.id)) return;

  const content = getTitleAndBody(notification);
  if (!content) return;

  const browserNotification = new Notification(content.title, {
    body: content.body,
    tag: notification.id,
    icon: "/fraction-icon.png",
  });

  browserNotification.onclick = () => {
    window.focus();
    browserNotification.close();
  };
}
